/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useRef } from "react";

/** One poll: answers whether to poll again; `isCurrent` turns false once its answer is stale. */
export type PollTick = (isCurrent: () => boolean) => Promise<boolean>;

/**
 * Runs `tick` while `active`, each run starting `delayMillis` after the previous one settled, so
 * reads never overlap and an older answer never lands after a newer one. The first run waits
 * `delayMillis` too unless `immediate`. Unmounting or turning inactive starts no further run.
 */
export function usePolling(
  tick: PollTick,
  { delayMillis, active = true, immediate = false }: {
    delayMillis: number;
    active?: boolean;
    immediate?: boolean;
  },
): void {
  const tickRef = useRef(tick);
  tickRef.current = tick;

  useEffect(() => {
    if (!active) {
      return;
    }
    let current = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const isCurrent = () => current;
    const schedule = () => {
      timer = setTimeout(() => void run(), delayMillis);
    };
    const run = async () => {
      const again = await tickRef.current(isCurrent);
      if (again && current) {
        schedule();
      }
    };
    if (immediate) {
      void run();
    } else {
      schedule();
    }
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [active, delayMillis, immediate]);
}
